import type { ReactNode } from 'react';
import { cssVars, cx } from '../design-system';
import styles from './AbsenceTypePicker.module.css';

/**
 * The picker that chooses which kind of absence a screen is showing.
 *
 * It began as «Mis ausencias»' own markup and moved here when Aprobaciones
 * adopted it. The two screens ask the same question — vacations, permits or
 * certificates — and were answering it with two different controls: cards here, a
 * segmented control there. Two controls for one question is a thing the reader has
 * to learn twice.
 *
 * The card carries a subtitle because the labels alone are ambiguous in this
 * product: «Permisos» is home office, hours and personal days, and nobody guesses
 * that from the word. The count badge appears only when there is something
 * waiting — a zero is not news, and a row of zeroes trains people to stop looking.
 */

export interface AbsenceTypeOption<Id extends string> {
  readonly id: Id;
  readonly title: string;
  readonly note: string;
  readonly glyph: string;
  readonly color: string;
  /** Shown as a badge when above zero. */
  readonly count?: number;
}

export const AbsenceTypePicker = <Id extends string>({
  options,
  active,
  onSelect,
  label = 'Tipo de ausencia',
}: {
  options: readonly AbsenceTypeOption<Id>[];
  active: Id;
  onSelect: (id: Id) => void;
  label?: string;
}): ReactNode => (
  <div className={styles.picker} role="tablist" aria-label={label}>
    {options.map((option) => (
      <button
        key={option.id}
        type="button"
        role="tab"
        aria-selected={option.id === active}
        className={cx(styles.card, option.id === active && styles.cardActive)}
        style={cssVars({ '--tab-color': option.color })}
        onClick={() => {
          onSelect(option.id);
        }}
      >
        <span
          className={styles.icon}
          style={cssVars({ '--icon-color': option.color })}
          aria-hidden="true"
        >
          {option.glyph}
        </span>
        <span className={styles.cardText}>
          <span className={styles.cardTitle}>{option.title}</span>
          <span className={styles.cardNote}>{option.note}</span>
        </span>
        {option.count !== undefined && option.count > 0 && (
          <span className={styles.badge}>{option.count}</span>
        )}
      </button>
    ))}
  </div>
);
